import type { StandardSchemaV1 } from "@standard-schema/spec";
import {
  getCurrentDocument,
  takeSelectionSnapshot,
  setSelectionToDOM,
  getPointedCaretPosition,
  defaultIsBlockNode,
  serializeRange,
} from "./dom/index.js";
import { createMutationObserver } from "./dom/mutation.js";
import type { DocNode, Fragment, Selection } from "./doc/types.js";
import { is, isFunction, isString, microtask } from "./utils.js";
import {
  domSelectionToSelection,
  positionToOffset,
  isBlockNode,
  isTextNode,
} from "./doc/node.js";
import {
  applyOperation,
  type Operation,
  isUnsafeOperation,
  isValidSelection,
  mapPositionWithOps,
} from "./doc/operation.js";
import { createParser } from "./dom/index.js";
import { isCollapsed, toRange } from "./doc/position.js";
import { historyPlugin } from "./plugins/history.js";
import type { Parser } from "./dom/parser.js";

const empty: unknown[] = [];

const noop = () => {};

const defaultOnError = (message: string): never => {
  throw new Error(message);
};

/**
 * https://www.w3.org/TR/input-events-1/#interface-InputEvent-Attributes
 */
type InputType =
  | "insertText" // insert typed plain text
  | "insertReplacementText" // replace existing text by means of a spell checker, auto-correct or similar
  | "insertLineBreak" // insert a line break
  | "insertParagraph" // insert a paragraph break
  | "insertOrderedList" // insert a numbered list
  | "insertUnorderedList" // insert a bulleted list
  | "insertHorizontalRule" // insert a horizontal rule
  | "insertFromYank" // replace the current selection with content stored in a kill buffer
  | "insertFromDrop" // insert content into the DOM by means of drop
  | "insertFromPaste" // paste
  | "insertFromPasteAsQuotation" // paste content as a quotation
  | "insertTranspose" // transpose the last two characters that were entered
  | "insertCompositionText" // replace the current composition string
  | "insertLink" // insert a link
  | "deleteWordBackward" // delete a word directly before the caret position
  | "deleteWordForward" // delete a word directly after the caret position
  | "deleteSoftLineBackward" // delete from the caret to the nearest visual line break before the caret position
  | "deleteSoftLineForward" // delete from the caret to the nearest visual line break after the caret position
  | "deleteEntireSoftLine" // delete from to the nearest visual line break before the caret position to the nearest visual line break after the caret position
  | "deleteHardLineBackward" // delete from the caret to the nearest beginning of a block element or br element before the caret position
  | "deleteHardLineForward" // delete from the caret to the nearest end of a block element or br element after the caret position
  | "deleteByDrag" // remove content from the DOM by means of drag
  | "deleteByCut" // remove the current selection as part of a cut
  | "deleteContent" // delete the selection without specifying the direction of the deletion and this intention is not covered by another inputType
  | "deleteContentBackward" // delete the content directly before the caret position and this intention is not covered by another inputType or delete the selection with the selection collapsing to its start after the deletion
  | "deleteContentForward" // delete the content directly after the caret position and this intention is not covered by another inputType or delete the selection with the selection collapsing to its end after the deletion
  | "historyUndo" // undo the last editing action
  | "historyRedo" // to redo the last undone editing action
  | "formatBold" // initiate bold text
  | "formatItalic" // initiate italic text
  | "formatUnderline" // initiate underline text
  | "formatStrikeThrough" // initiate stricken through text
  | "formatSuperscript" // initiate superscript text
  | "formatSubscript" // initiate subscript text
  | "formatJustifyFull" // make the current selection fully justified
  | "formatJustifyCenter" // center align the current selection
  | "formatJustifyRight" // right align the current selection
  | "formatJustifyLeft" // left align the current selection
  | "formatIndent" // indent the current selection
  | "formatOutdent" // outdent the current selection
  | "formatRemove" // remove all formatting from the current selection
  | "formatSetBlockTextDirection" // set the text block direction
  | "formatSetInlineTextDirection" // set the text inline direction
  | "formatBackColor" // change the background color
  | "formatFontColor" // change the font color
  | "formatFontName" // change the font-family
  // Legacy events older Chrome/Safari may dispatch
  // https://github.com/w3c/input-events/pull/122
  | "deleteCompositionText"
  | "deleteByComposition"
  | "insertFromComposition";

type EditorCommandOrPlugin<A extends unknown[], T extends DocNode = DocNode> = (
  editor: Editor<T>,
  ...args: A
) => void | undefined;

type EditorQuery<A extends unknown[], V, T extends DocNode = DocNode> = (
  editor: Editor<T>,
  ...args: A
) => V;

/**
 * Options of {@link createEditor}.
 */
export interface EditorOptions<
  T extends DocNode,
  S extends StandardSchemaV1<T, T> | void = void,
> {
  /**
   * Optional [Standard Schema](https://github.com/standard-schema/standard-schema) to validate unsafe edits.
   */
  schema?: S;
  /**
   * Initial document.
   */
  doc: T;
  /**
   * The state editable or not.
   */
  readonly?: boolean;
  /**
   * TODO
   */
  isBlock?: (node: HTMLElement) => boolean;
  /**
   * Keep the caret visible inside the mounted element after document
   * changes, behaving like a native `<textarea>`:
   *
   * - If the caret is already visible (e.g. typing in the middle of a
   *   long doc), nothing scrolls.
   * - If the caret falls below the viewport, the element scrolls down
   *   just enough to reveal it.
   * - If the caret falls above the viewport, the element scrolls up
   *   just enough to reveal it.
   *
   * Scroll work is coalesced via `requestAnimationFrame` and only reads
   * the caret's bounding rect, never `scrollHeight`, so it does not
   * force a full overflow-layout pass on each input.
   *
   * @default false
   */
  autoScroll?: boolean;
  /**
   * Revert DOM mutations that are not from IME composition and not inside
   * {@link Editor.domUpdate}.
   *
   * Use with **imperative** view sync (host patches the contenteditable from
   * `change`). Declarative React/Vue hosts that re-render from state should
   * leave this `false` — their paint looks like a foreign mutation and would
   * be undone.
   *
   * When enabled, wrap every host-driven DOM write in {@link Editor.domUpdate}
   * so the paint is accepted. This defends against page translators,
   * Grammarly-like extensions, and similar tools that rewrite the editable
   * tree and desync it from the document model.
   *
   * @default false
   */
  revertForeignMutations?: boolean;
  /**
   * Callback invoked when errors happen.
   *
   * @default console.warn
   */
  onWarn?: (message: string) => void;
  /**
   * Callback invoked when errors happen.
   *
   * @default `throw new Error(message)`
   */
  onError?: (message: string) => never;
}

export type EditorContext<_> = {};

type EditorEventMap = {
  change: () => void;
  selectionchange: () => void;
  readonly: () => void;
};

/**
 * Functions to handle keyboard events.
 *
 * Return `true` if you want to stop propagation.
 */
export type KeyboardHook = (keyboard: KeyboardEvent) => boolean | void;

/**
 * Functions to handle copy events
 */
export type CopyHook = (dataTransfer: DataTransfer) => void;

/**
 * Functions to handle paste / drop events.
 *
 * Return `true` if you want to stop propagation.
 */
export type PasteHook = (
  dataTransfer: DataTransfer,
) => string | Fragment | true | null;

type EditorHookMap = {
  /**
   * Call `next(op)` to continue applying the operation, or `next()` with a nullish value to cancel it.
   * If the hook returns without calling `next`, the operation is passed through as is.
   */
  apply: (op: Operation, next: (op?: Operation | null) => void) => void;
  mount: (element: HTMLElement, parser: Parser) => void | (() => void);
  keyboard: KeyboardHook;
  copy: CopyHook;
  paste: PasteHook;
};

/**
 * The editor instance.
 */
export interface Editor<T extends DocNode = DocNode> {
  readonly doc: T;
  /**
   * Whether the document is empty (no text content, no void nodes).
   * Recomputed once per commit so reads stay O(1).
   */
  readonly isEmpty: boolean;
  selection: Selection;
  /**
   * The getter/setter for the editor's read-only state.
   * `true` to read-only. `false` to editable.
   */
  readonly: boolean;
  /**
   * Enable/disable native-textarea-like auto-scroll: scroll the mounted
   * element only when needed to keep the caret visible.
   *
   * @see {@link EditorOptions.autoScroll}
   */
  autoScroll: boolean;
  /**
   * Dispatches editing operations.
   * @param op {@link Operation}
   */
  apply(op: Operation | Operation[]): this;
  /**
   * Executes a function with editor bound as context.
   * @param fn {@link EditorCommandOrPlugin} or {@link EditorQuery}
   * @param args arguments of the function
   */
  exec<const A extends unknown[]>(
    fn: EditorCommandOrPlugin<A, T>,
    ...args: A
  ): this;
  exec<const A extends unknown[], V>(fn: EditorQuery<A, V, T>, ...args: A): V;
  /**
   * A function to subscribe editor events.
   * @returns cleanup function
   */
  on<K extends keyof EditorEventMap>(
    key: K,
    callback: EditorEventMap[K],
  ): () => void;
  /**
   * A function to register editor hooks.
   * @returns cleanup function
   */
  hook<K extends keyof EditorHookMap>(
    key: K,
    callback: EditorHookMap[K],
  ): () => void;
  /**
   * Get a value from the context.
   */
  get<V>(key: EditorContext<V>): V;
  /**
   * Set a value for the context.
   */
  set<V>(key: EditorContext<V>, value: V): this;
  /**
   * A function to make DOM editable.
   * @returns A function to stop subscribing DOM changes and restores previous DOM state.
   */
  input: (element: HTMLElement) => () => void;
  /**
   * Run a host-driven DOM paint. Mutations inside are accepted by the
   * MutationObserver.
   *
   * Required when {@link EditorOptions.revertForeignMutations} is `true`.
   * Safe to call when unmounted (runs `fn` only) or when the option is off.
   */
  domUpdate(fn: () => void): this;
}

/**
 * A function to initialize {@link Editor}.
 */
export const createEditor = <
  T extends DocNode,
  S extends StandardSchemaV1<T, T> | void = void,
>({
  doc,
  readonly = false,
  schema,
  isBlock = defaultIsBlockNode,
  autoScroll: _autoScroll = false,
  revertForeignMutations = false,
  onWarn = console.warn,
  onError = defaultOnError,
}: EditorOptions<T, S>): Editor<T> => {
  let selection: Selection = [0, 0];
  let mountedElement: HTMLElement | null = null;
  let scrollRAF = 0;
  /** Set while `input()` is active — host paints go through here. */
  let runDomUpdate: ((fn: () => void) => void) | null = null;

  const scheduleScroll = () => {
    if (mountedElement && !scrollRAF) {
      const element = mountedElement;
      scrollRAF = requestAnimationFrame(() => {
        scrollRAF = 0;
        // Keep the caret visible inside the scroll container without jumping to
        // the bottom on every change. We avoid reading `scrollHeight` here
        // because it forces a full overflow layout pass; instead we measure the
        // caret rect (cheap, incremental) and scroll only when it falls
        // outside the visible viewport of the mounted element.
        const win = element.ownerDocument.defaultView;
        const domSel = win?.getSelection();
        if (!domSel || domSel.rangeCount === 0) return;
        const range = domSel.getRangeAt(0);
        if (!element.contains(range.startContainer)) return;

        let rect = range.getBoundingClientRect();
        // Collapsed ranges next to void/inline-block siblings can report a
        // zero rect in some browsers — fall back to the focus node's rect.
        if (rect.top === 0 && rect.bottom === 0 && rect.height === 0) {
          const node = range.startContainer;
          const probe =
            node.nodeType === 1 ? (node as Element) : node.parentElement;
          if (!probe) return;
          rect = probe.getBoundingClientRect();
        }

        const host = element.getBoundingClientRect();
        const margin = 4;
        if (rect.top < host.top + margin) {
          element.scrollTop -= host.top + margin - rect.top;
        } else if (rect.bottom > host.bottom - margin) {
          element.scrollTop += rect.bottom - (host.bottom - margin);
        }
      });
    }
  };

  // O(n) walk over the doc; cached after each commit so `editor.isEmpty`
  // remains O(1) for callers. Supports both block docs and single-line
  // (inline-children) docs from upstream.
  const computeEmpty = (value: DocNode): boolean => {
    for (const child of value.children) {
      if (isBlockNode(child)) {
        for (const node of child.children) {
          if (!isTextNode(node) || node.text.length > 0) {
            return false;
          }
        }
      } else if (!isTextNode(child) || child.text.length > 0) {
        return false;
      }
    }
    return true;
  };
  let emptyDoc = computeEmpty(doc);

  const validate = (value: T): boolean => {
    if (!schema) {
      onWarn(
        "An unsafe operation was detected. We recommend using schema option.",
      );
      return true;
    }
    const result = schema["~standard"].validate(value);
    if (result instanceof Promise) {
      onError("async validate is not supported");
    } else if (result.issues) {
      onWarn(result.issues.map((i) => i.message).join("\n"));
    } else {
      return true;
    }
    return false;
  };

  if (schema && !validate(doc)) {
    onError("Invalid document");
  }

  const hooks = new Map<
    keyof EditorHookMap,
    EditorHookMap[keyof EditorHookMap][]
  >();

  const contexts = new WeakMap<EditorContext<unknown>, unknown>();

  const getHook = <T extends keyof EditorHookMap>(
    key: T,
  ): readonly EditorHookMap[T][] => {
    return (hooks.get(key) || empty) as unknown as EditorHookMap[T][];
  };

  const subs = new Map<
    keyof EditorEventMap,
    Set<EditorEventMap[keyof EditorEventMap]>
  >();

  const publishing = new Set<() => void>();

  const publish = <K extends keyof EditorEventMap>(key: K) => {
    const sub = subs.get(key);
    if (sub) {
      if (!publishing.size) {
        microtask(() => {
          publishing.forEach((cb) => {
            cb();
          });
          publishing.clear();
        });
      }
      sub.forEach((s) => {
        publishing.add(s);
      });
    }
  };

  const apply = (op: Operation | Operation[]) => {
    if (!readonly) {
      if (Array.isArray(op)) {
        for (const o of op) {
          applyOp(o);
        }
      } else {
        applyOp(op);
      }
    }
    return editor;
  };

  const applyOp = (op: Operation) => {
    const currentDoc = doc;
    const applyHooks = getHook("apply");
    const length = applyHooks.length;

    let index = 0;

    const dispatch = () => {
      if (index < length) {
        const i = index;
        applyHooks[index]!(op, next);
        if (i === index) {
          next(op);
        }
      } else if (index === length) {
        index++;

        try {
          const [nextDoc, nextSelection] = applyOperation(doc, selection, op);
          if (!isUnsafeOperation(op) || validate(nextDoc)) {
            doc = nextDoc;
            updateSelection(nextSelection);
          }
        } catch (e) {
          // rollback
          onWarn("rollback operation: " + e);
        }
      }
    };

    const next = (o?: Operation | null): void => {
      if (o == null) {
        // cancel
        index = length + 1;
        return;
      }
      op = o;
      index++;
      dispatch();
    };

    dispatch();

    if (!is(currentDoc, doc)) {
      emptyDoc = computeEmpty(doc);
      publish("change");
    }
  };

  const updateSelection = (s: Selection) => {
    if (
      isValidSelection(doc, s) &&
      (selection[0] !== s[0] || selection[1] !== s[1])
    ) {
      selection = s;
      publish("selectionchange");
    }
  };

  const editor: Editor<T> = {
    get doc() {
      return doc;
    },
    get isEmpty() {
      return emptyDoc;
    },
    get selection() {
      return selection;
    },
    set selection(value) {
      updateSelection(value);
    },
    get readonly() {
      return readonly;
    },
    set readonly(value) {
      readonly = value;
      publish("readonly");
    },
    get autoScroll() {
      return _autoScroll;
    },
    set autoScroll(value) {
      _autoScroll = value;
    },
    apply,
    on: (type, callback) => {
      let sub = subs.get(type);
      if (!sub) {
        subs.set(type, (sub = new Set()));
      }
      sub.add(callback);
      return () => {
        sub.delete(callback);
      };
    },
    hook: (type, callback) => {
      let sub = hooks.get(type);
      if (!sub) {
        hooks.set(type, (sub = []));
      }
      sub.push(callback);
      return () => {
        const i = sub.indexOf(callback);
        if (i !== -1) {
          sub.splice(i, 1);
        }
      };
    },
    exec: (
      fn: EditorCommandOrPlugin<any, T> | EditorQuery<any, unknown, T>,
      ...args: unknown[]
    ): any => {
      const result = fn(editor, ...args);
      if (typeof result === "undefined") {
        return editor;
      }
      return result;
    },
    get: (key) => {
      if (!contexts.has(key)) {
        onError("No value found for key");
      }
      return contexts.get(key) as any;
    },
    set: (key, value) => {
      contexts.set(key, value);
      return editor;
    },
    domUpdate: (fn) => {
      if (runDomUpdate) {
        runDomUpdate(fn);
      } else {
        fn();
      }
      return editor;
    },
    input: (element) => {
      if (
        !(window.InputEvent && isFunction(InputEvent.prototype.getTargetRanges))
      ) {
        onWarn("beforeinput event is not supported.");
        return noop;
      }
      mountedElement = element;

      // https://w3c.github.io/contentEditable/
      // https://w3c.github.io/editing/docs/execCommand/
      // https://w3c.github.io/selection-api/
      const {
        contentEditable: prevContentEditable,
        role: prevRole,
        ariaMultiLine: prevAriaMultiLine,
        ariaReadOnly: prevAriaReadOnly,
      } = element;
      const prevWhiteSpace = element.style.whiteSpace;

      element.role = "textbox";
      // https://html.spec.whatwg.org/multipage/interaction.html#best-practices-for-in-page-editors
      element.style.whiteSpace = "pre-wrap";
      element.ariaMultiLine = "true";

      let disposed = false;
      let inputTransaction: [Operation[], Selection] | null = null;
      let isComposing = false;
      let hasFocus = false;
      let isDragging = false;
      let domSelection: Selection = selection;
      let syncDomSelectionTimer: ReturnType<typeof setTimeout> | null = null;

      const document = getCurrentDocument(element);

      const parser = createParser(
        document,
        isBlock as (node: Element) => boolean,
      );

      const setEditableState = () => {
        element.contentEditable = readonly ? "false" : "true";
        element.ariaReadOnly = readonly ? "true" : null;
      };

      setEditableState();

      const syncFocus = () => {
        if (!hasFocus) {
          // Set focus imperatively to return focus to the editor after a command execution via click.
          // It must be queued after the MO callback because that may cause an additional selectionchange event.
          element.focus({ preventScroll: true });
        }
      };
      const syncDomSelection = () => {
        syncDomSelectionTimer = null;
        if (
          selection[0] !== domSelection[0] ||
          selection[1] !== domSelection[1]
        ) {
          setSelectionToDOM(element, parser, doc, selection);
          domSelection = selection;
        }
      };
      const cancelSyncDomSelection = () => {
        if (syncDomSelectionTimer != null) {
          clearTimeout(syncDomSelectionTimer);
        }
      };

      const cleanupOnChange = editor.on("change", () => {
        if (!hasFocus) {
          requestAnimationFrame(syncFocus);
        }
      });
      const cleanupOnSelectionChange = editor.on("selectionchange", () => {
        if (
          selection[0] !== domSelection[0] ||
          selection[1] !== domSelection[1]
        ) {
          cancelSyncDomSelection();
          // Use setTimeout to ensure synchronization is done after the mutation caused by re-render, especially on Firefox
          syncDomSelectionTimer = setTimeout(syncDomSelection, 50);
        }
      });
      const cleanupOnReadonly = editor.on("readonly", setEditableState);

      const paste = (dataTransfer: DataTransfer): string | Fragment | void => {
        for (const ex of getHook("paste")) {
          const pasted = ex(dataTransfer);
          if (pasted) {
            if (pasted === true) {
              return;
            }
            return pasted;
          }
        }
        onWarn("failed to serialize pasted data");
      };

      const restoreDomSelection = () => {
        cancelSyncDomSelection();
        // TODO optimize
        // Mutation to selected DOM may change selection, so restore it.
        setSelectionToDOM(element, parser, doc, selection);
      };

      const observer = createMutationObserver(
        element,
        restoreDomSelection,
        revertForeignMutations,
      );

      runDomUpdate = (fn) => {
        observer._domUpdate(true);
        try {
          fn();
        } finally {
          observer._domUpdate(false);
          restoreDomSelection();
        }
      };

      const syncSelection = () => {
        updateSelection(
          (domSelection = domSelectionToSelection(
            doc,
            takeSelectionSnapshot(element, parser),
          )),
        );
      };

      const flushInput = () => {
        const queue = observer._flush();

        observer._record(false);

        if (queue.length) {
          observer._revert(queue);

          // Restore previous selection
          // Updating selection may schedule the next selectionchange event
          // It should be ignored especially in firefox not to confuse editor state
          document.removeEventListener("selectionchange", onSelectionChange);
          setSelectionToDOM(element, parser, doc, selection, true);
          document.addEventListener("selectionchange", onSelectionChange);
        }

        if (inputTransaction) {
          updateSelection(inputTransaction[1]);
          apply(inputTransaction[0]);
          inputTransaction = null;
        }
        isComposing = false;
      };

      // spec compliant: keydown -> beforeinput -> input (-> keyup)
      // Safari (IME)  : beforeinput -> input -> keydown (-> keyup)
      // https://w3c.github.io/uievents/#events-keyboard-event-order
      // https://bugs.webkit.org/show_bug.cgi?id=165004
      const onKeyDown = (e: KeyboardEvent) => {
        if (isComposing) return;

        for (const handler of getHook("keyboard")) {
          if (handler(e)) {
            e.preventDefault();
            observer._record(false);
            return;
          }
        }
      };

      const onInput = () => {
        if (!isComposing) {
          flushInput();
        }
      };
      const onBeforeInput = (e: InputEvent) => {
        e.preventDefault();

        const inputType = e.inputType as InputType;

        if (inputType.startsWith("format")) {
          // Ignore format inputs from document.execCommand() or shortcuts like mod+b.
          return;
        }
        if (inputType === "historyUndo" || inputType === "historyRedo") {
          // Cancel for now.
          return;
        }

        if (isComposing) {
          // Unfortunately, input events related to composition are not cancellable.
          // So we record mutations to DOM and revert them after composition ended.
          observer._record(true);
        } else {
          syncSelection();
        }

        const domRange = e.getTargetRanges()[0];
        if (domRange) {
          // Read input
          let data =
            inputType === "insertParagraph" || inputType === "insertLineBreak"
              ? "\n"
              : e.data;
          if (data == null) {
            const dataTransfer = e.dataTransfer;
            if (dataTransfer) {
              // In some cases (e.g. insertReplacementText), dataTransfer contains text.
              data = dataTransfer.getData("text/plain");
            }
          }

          if (!inputTransaction) {
            inputTransaction = [[], selection];
          }
          const ops = inputTransaction[0];

          const range = domSelectionToSelection(
            doc,
            serializeRange(element, parser, domRange),
          );
          if (!isCollapsed(range)) {
            // replace or delete
            ops.push({ type: "delete", range });
          }
          if (data) {
            // replace or insert
            ops.push({ type: "insert_text", at: range[0], text: data });
          }
        }

        if (!isComposing) {
          flushInput();
        }
      };
      const onCompositionStart = () => {
        if (!isComposing) {
          syncSelection();
        }
        isComposing = true;
      };
      const onCompositionEnd = () => {
        flushInput();
      };

      const onFocus = () => {
        hasFocus = true;
        syncSelection();
      };
      const onBlur = () => {
        hasFocus = false;
      };

      const onSelectionChange = () => {
        // Safari may dispatch selectionchange event after dragstart
        if (hasFocus && !isComposing && !isDragging) {
          syncSelection();
        }
      };

      const copy = (dataTransfer: DataTransfer) => {
        syncSelection();
        if (!isCollapsed(selection)) {
          for (const ex of getHook("copy")) {
            ex(dataTransfer);
          }
        }
      };

      const onCopy = (e: ClipboardEvent) => {
        e.preventDefault();
        copy(e.clipboardData!);
      };
      const onCut = (e: ClipboardEvent) => {
        e.preventDefault();
        if (!readonly) {
          copy(e.clipboardData!);
          apply({ type: "delete", range: toRange(selection) });
        }
      };
      const onPaste = (e: ClipboardEvent) => {
        e.preventDefault();
        const pasted = paste(e.clipboardData!);
        if (pasted) {
          const range = toRange(selection);
          const start = range[0];
          const ops: Operation[] = [{ type: "delete", range }];
          ops.push(
            isString(pasted)
              ? { type: "insert_text", at: start, text: pasted }
              : { type: "insert_node", at: start, fragment: pasted },
          );
          apply(ops);
        }
      };

      const onDrop = (e: DragEvent) => {
        e.preventDefault();

        const dataTransfer = e.dataTransfer;
        const droppedPosition = getPointedCaretPosition(element, parser, e);
        if (dataTransfer && droppedPosition) {
          let afterSelection: Selection | undefined;
          const ops: Operation[] = [];
          if (isDragging) {
            ops.push({ type: "delete", range: toRange(selection) });
          }
          const pasted = paste(dataTransfer);
          if (pasted) {
            const offset = positionToOffset(doc, droppedPosition);
            const pos = mapPositionWithOps(offset, ops);
            ops.push(
              isString(pasted)
                ? { type: "insert_text", at: pos, text: pasted }
                : { type: "insert_node", at: pos, fragment: pasted },
            );
            afterSelection = [pos, mapPositionWithOps(offset, ops)];
          }
          apply(ops);
          if (afterSelection) {
            updateSelection(afterSelection);
          }
        }

        // dragend event may not fire when the drop target node is re-rendered
        isDragging = false;
      };
      const onDragStart = (e: DragEvent) => {
        isDragging = true;
        copy(e.dataTransfer!);
      };
      const onDragEnd = () => {
        isDragging = false;
      };

      document.addEventListener("selectionchange", onSelectionChange);
      element.addEventListener("keydown", onKeyDown);
      element.addEventListener("input", onInput);
      element.addEventListener("beforeinput", onBeforeInput);
      element.addEventListener("compositionstart", onCompositionStart);
      element.addEventListener("compositionend", onCompositionEnd);
      element.addEventListener("focus", onFocus);
      element.addEventListener("blur", onBlur);
      element.addEventListener("copy", onCopy);
      element.addEventListener("cut", onCut);
      element.addEventListener("paste", onPaste);
      element.addEventListener("drop", onDrop);
      element.addEventListener("dragstart", onDragStart);
      element.addEventListener("dragend", onDragEnd);

      const unmountHooks: (() => void)[] = [];
      getHook("mount").forEach((mount) => {
        const cb = mount(element, parser);
        if (cb) {
          unmountHooks.push(cb);
        }
      });

      return () => {
        if (disposed) return;
        disposed = true;

        cleanupOnChange();
        cleanupOnSelectionChange();
        cleanupOnReadonly();

        if (scrollRAF) {
          cancelAnimationFrame(scrollRAF);
          scrollRAF = 0;
        }
        if (mountedElement === element) {
          mountedElement = null;
        }
        if (runDomUpdate) {
          runDomUpdate = null;
        }

        element.contentEditable = prevContentEditable;
        element.role = prevRole;
        element.ariaMultiLine = prevAriaMultiLine;
        element.ariaReadOnly = prevAriaReadOnly;
        element.style.whiteSpace = prevWhiteSpace;

        observer._dispose();

        document.removeEventListener("selectionchange", onSelectionChange);
        element.removeEventListener("keydown", onKeyDown);
        element.removeEventListener("input", onInput);
        element.removeEventListener("beforeinput", onBeforeInput);
        element.removeEventListener("compositionstart", onCompositionStart);
        element.removeEventListener("compositionend", onCompositionEnd);
        element.removeEventListener("focus", onFocus);
        element.removeEventListener("blur", onBlur);
        element.removeEventListener("copy", onCopy);
        element.removeEventListener("cut", onCut);
        element.removeEventListener("paste", onPaste);
        element.removeEventListener("drop", onDrop);
        element.removeEventListener("dragstart", onDragStart);
        element.removeEventListener("dragend", onDragEnd);

        unmountHooks.forEach((cb) => {
          cb();
        });
      };
    },
  };

  editor.exec(historyPlugin);

  editor.on("change", () => {
    if (_autoScroll) {
      scheduleScroll();
    }
  });

  return editor;
};
